// Shared page shell for the legal documents (Privacy Policy, Terms of
// Service). The markdown sources live in src/content/*.md and are imported
// raw via Vite's ?raw query so we don't need a build-time MDX pipeline. The
// public route /privacy and /terms each mount this component with the right
// document.
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import privacyMd from "../content/privacy-policy.md?raw";
import termsMd from "../content/terms-of-service.md?raw";
import { JobWarpIcon } from "../components/JobWarpIcon.js";
import { Seo } from "../components/Seo.js";

const DOCS = {
  privacy: {
    body: privacyMd,
    path: "/privacy/",
    seoTitle: "Privacy Policy — JobWarp",
    seoDescription:
      "How JobWarp handles your personal data: what we collect, our sub-processors, international transfers, and your GDPR rights.",
  },
  terms: {
    body: termsMd,
    path: "/terms/",
    seoTitle: "Terms of Service — JobWarp",
    seoDescription:
      "The terms governing your use of JobWarp, including AI-generated content, sub-processors, acceptable use, and liability.",
  },
} as const;

export function LegalPage({ doc }: { doc: keyof typeof DOCS }) {
  const { body, path, seoTitle, seoDescription } = DOCS[doc];
  return (
    <div className="min-h-screen flex flex-col bg-page">
      <Seo title={seoTitle} description={seoDescription} path={path} />
      {/* Reuse the marketing site's minimal header so signed-out visitors
          land on something that looks like JobWarp, not a raw document. */}
      <header className="border-b border-default">
        <div className="max-w-[960px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-primary">
            <JobWarpIcon size={20} />
            <span className="font-serif italic text-[20px] tracking-[-0.01em] leading-none">JobWarp</span>
          </Link>
          <nav className="flex gap-4 text-13">
            <Link to="/privacy/" className={`hover:text-primary ${doc === "privacy" ? "text-primary" : "text-secondary"}`}>
              Privacy
            </Link>
            <Link to="/terms/" className={`hover:text-primary ${doc === "terms" ? "text-primary" : "text-secondary"}`}>
              Terms
            </Link>
          </nav>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-[760px] mx-auto px-6 py-10">
          {/* The markdown body opens with a single `#` heading, which renders
              as the page's sole <h1>. No extra sr-only <h1> here — two <h1>
              tags trip Ahrefs' "Multiple H1 tags" check. */}
          <article className="legal-prose">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
          </article>
        </div>
      </main>

      <footer className="border-t border-default">
        <div className="max-w-[960px] mx-auto px-6 py-6 flex flex-wrap items-center justify-between gap-4 text-tertiary text-11 font-mono uppercase tracking-mono-caps">
          <span>© 2026 JobWarp · Built for job seekers</span>
          <span className="flex gap-4">
            <Link to="/privacy/" className="hover:text-primary">Privacy</Link>
            <Link to="/terms/" className="hover:text-primary">Terms</Link>
          </span>
        </div>
      </footer>
    </div>
  );
}
